/**
 * Настройки проекта — черновик на весь экран.
 *
 * Правки копятся локально и уходят одной кнопкой, как в макете: у настроек
 * доски нет ни одного изменения, которое имело бы смысл в одиночку —
 * переставленная колонка без нового имени и лимита это половина работы.
 * Черновик один на все вкладки: переключение вкладки не должно терять набранное.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { markColumnsTouched } from '@/features/onboarding/api'
import { getProject, type Project } from '@/features/projects/api'

import {
  applyPreset,
  loadSettings,
  saveColumns,
  saveMembers,
  saveProject,
  type SettingsColumn,
  type SettingsMember,
} from './api'

import type {
  TrackerColumnPreset,
  V1MemberRole,
  V1StatusCategory,
  V1TypeProfile,
} from '@/api/clients/tracker-project'

export interface GeneralForm {
  name: string
  description: string
  isPrivate: boolean
  aiEnabled: boolean
  typeProfile: V1TypeProfile | ''
}

/** Слепок для сравнения: что именно экран считает изменённым */
const snapshot = (
  columns: SettingsColumn[],
  members: SettingsMember[],
  form: GeneralForm,
): string =>
  JSON.stringify({
    columns: columns.map((column) => [
      column.id,
      column.name,
      column.color,
      column.statusCategory,
      column.wipLimit,
    ]),
    members: members.map((member) => [member.userId, member.role]),
    form,
  })

export const useSettingsStore = defineStore('project-settings', () => {
  const project = ref<Project | null>(null)
  const columns = ref<SettingsColumn[]>([])
  const members = ref<SettingsMember[]>([])
  const form = ref<GeneralForm>({
    name: '',
    description: '',
    isPrivate: false,
    aiEnabled: false,
    typeProfile: '',
  })

  /** Что приехало с сервера: по нему считаются удаления и правки */
  const loadedColumns = ref<SettingsColumn[]>([])
  const loadedMembers = ref<SettingsMember[]>([])
  const loadedSnapshot = ref('')

  const isLoading = ref(false)
  const isSaving = ref(false)
  const error = ref<unknown>(null)

  const isDirty = computed(
    () => snapshot(columns.value, members.value, form.value) !== loadedSnapshot.value,
  )

  /** Всего задач в проекте — от него зависит, ляжет ли пресет */
  const issueTotal = computed(() =>
    columns.value.reduce((total, column) => total + column.issueCount, 0),
  )

  /** Настройки правит только администратор проекта — так же считает и сервис */
  const isAdmin = computed(() => project.value?.myRole === 'MEMBER_ROLE_ADMIN')

  const fill = (next: {
    project: Project
    columns: SettingsColumn[]
    members: SettingsMember[]
  }) => {
    project.value = next.project
    columns.value = next.columns.map((column) => ({ ...column }))
    members.value = next.members.map((member) => ({ ...member }))
    form.value = {
      name: next.project.name,
      description: next.project.description,
      isPrivate: next.project.isPrivate,
      aiEnabled: next.project.aiEnabled,
      typeProfile: next.project.typeProfile,
    }

    loadedColumns.value = next.columns.map((column) => ({ ...column }))
    loadedMembers.value = next.members.map((member) => ({ ...member }))
    loadedSnapshot.value = snapshot(columns.value, members.value, form.value)
  }

  const load = async (projectKey: string) => {
    isLoading.value = true
    error.value = null

    try {
      const found = await getProject(projectKey)

      fill(await loadSettings(found))
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Вход на экран. В отличие от доски и списка, черновик не переживает уход:
   * вернуться к настройкам с половиной незавершённых правок хуже, чем начать
   * с того, что на сервере.
   */
  const open = (projectKey: string) => load(projectKey)

  const reset = () => {
    if (!project.value) return

    fill({
      project: project.value,
      columns: loadedColumns.value,
      members: loadedMembers.value,
    })
  }

  const save = async () => {
    if (!project.value || isSaving.value) return

    isSaving.value = true

    try {
      const id = project.value.id

      await saveProject(id, form.value)
      await saveColumns({ projectId: id, columns: columns.value, loaded: loadedColumns.value })
      await saveMembers(id, members.value, loadedMembers.value)

      // Шаг чек-листа «настройте колонки» отмечается здесь: колонки у проекта
      // есть всегда, и отличить настроенные от пресетных по данным нельзя.
      markColumnsTouched()

      // Перечитываем целиком: у новых колонок появились идентификаторы, у
      // удалённых переехали задачи, а имя проекта видит ещё и сайдбар.
      await load(project.value.key)
    } finally {
      isSaving.value = false
    }
  }

  const addColumn = (name: string, color: string) => {
    columns.value = [
      ...columns.value,
      { id: '', name, color, statusCategory: 'STATUS_CATEGORY_OPEN', wipLimit: 0, issueCount: 0 },
    ]
  }

  /**
   * Удаление откладывается до сохранения, а вот куда девать задачи — вопрос
   * сегодняшний: после ухода строки с экрана их число уже не видно.
   */
  const removeColumn = (index: number, moveIssuesTo = '') => {
    const column = columns.value[index]

    if (!column) return

    if (column.id) {
      const target = loadedColumns.value.find((entry) => entry.id === column.id)

      if (target) target.moveIssuesTo = moveIssuesTo
    }

    columns.value = columns.value.filter((_, at) => at !== index)
  }

  const patchColumn = (index: number, change: Partial<SettingsColumn>) => {
    const column = columns.value[index]

    if (column) Object.assign(column, change)
  }

  const setRole = (userId: string, role: V1MemberRole) => {
    const member = members.value.find((entry) => entry.userId === userId)

    if (member) member.role = role
  }

  const addMember = (member: SettingsMember) => {
    if (members.value.some((entry) => entry.userId === member.userId)) return

    members.value = [...members.value, member]
  }

  /** Удаление, как и всё остальное на экране, уходит на сервер по «Сохранить». */
  const removeMember = (userId: string) => {
    members.value = members.value.filter((entry) => entry.userId !== userId)
  }

  /**
   * Пресет применяется сразу, мимо черновика: он заменяет доску на сервере
   * целиком, и «отменить» после него было бы нечем.
   */
  const usePreset = async (preset: TrackerColumnPreset) => {
    if (!project.value) return

    await applyPreset(project.value.id, preset)
    await load(project.value.key)
  }

  return {
    project,
    columns,
    members,
    form,
    isLoading,
    isSaving,
    error,
    isDirty,
    isAdmin,
    issueTotal,
    loadedColumns,
    open,
    load,
    reset,
    save,
    addColumn,
    removeColumn,
    patchColumn,
    setRole,
    addMember,
    removeMember,
    usePreset,
  }
})

export type { SettingsColumn, SettingsMember, V1MemberRole, V1StatusCategory }
